import { ReactNode, useCallback } from "react"
import {
  Block,
  BlockProps,
  LabelMedium,
  Link02,
  ListItem,
  ParagraphSmall,
  useStyletron,
} from "@mezo-org/mezo-clay"

export type LinkedPortalAccount = {
  artwork: string | null
  title: string
  description: string
  isLinked?: boolean
  indicator?: ReactNode
}

type OnboardingModalLinkedAccountProps = BlockProps & {
  accounts: LinkedPortalAccount[]
}

export default function OnboardingModalLinkedAccount({
  accounts,
  ...props
}: OnboardingModalLinkedAccountProps) {
  const [css, theme] = useStyletron()

  const getLinkedAccountImage = useCallback(
    (artwork: string | null, indicator?: ReactNode) => {
      if (!artwork) return null

      if (!indicator) {
        return (
          <Block
            as="img"
            src={artwork}
            className={css({
              width: "100%",
              height: "100%",
              objectFit: "cover",
              borderRadius: theme.borders.radius200,
              overflow: "hidden",
            })}
          />
        )
      }

      return (
        <Block position="relative">
          <Block
            as="img"
            src={artwork}
            className={css({
              width: "100%",
              height: "100%",
              objectFit: "cover",
              borderRadius: theme.borders.radius200,
              overflow: "hidden",
            })}
          />
          <Block
            className={css({
              position: "absolute",
              bottom: `-${theme.sizing.scale0}`,
              right: `-${theme.sizing.scale200}`,
            })}
          >
            {indicator}
          </Block>
        </Block>
      )
    },
    [css, theme],
  )

  return (
    <Block
      className={css({
        width: "100%",
        border: `1px solid ${theme.colors.borderOpaque}`,
        borderRadius: theme.borders.radius400,
        overflow: "hidden",
      })}
      {...props}
    >
      {accounts.map(
        ({ artwork, title, description, isLinked, indicator }, index) => (
          <ListItem
            key={title}
            endEnhancer={() =>
              isLinked ? (
                <Link02 color={theme.colors.contentStateDisabled} />
              ) : null
            }
            artwork={() => getLinkedAccountImage(artwork, indicator)}
            overrides={{
              ArtworkContainer: {
                style: () => ({
                  height: "72px",
                  width: "72px",
                  padding: theme.sizing.scale600,
                }),
              },
              ...(index === accounts.length - 1
                ? { Content: { style: () => ({ border: "none !important" }) } }
                : {}),
            }}
          >
            <Block
              className={css({
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
              })}
            >
              <LabelMedium marginBottom={theme.sizing.scale100}>
                {title}
              </LabelMedium>
              <ParagraphSmall color={theme.colors.contentSecondary}>
                {description}
              </ParagraphSmall>
            </Block>
          </ListItem>
        ),
      )}
    </Block>
  )
}
